/**
 * Olisé theme presets (plan §8.4 "Thèmes") translated into the Établi's shadcn
 * HSL tokens, for light and dark mode.
 *
 * Source: ../olise/frontend/src/styles/<theme>/tokens.css + theme.json.
 * Mapping: surface-page → background; surface-card → card, popover;
 * surface-sunken (light) / surface-raised (dark) → muted; text-body → foreground;
 * text-muted → muted-foreground; action → primary; action-soft → secondary;
 * accent-soft → accent; color-danger → destructive; border → border, input;
 * focus-ring → ring; radius-lg → --radius. Translucent dark tokens are
 * composited over the page surface.
 *
 * Precedence (see useApplyBranding): Établi defaults (index.css) < preset
 * (stylesheet with higher specificity) < the org's own branding (inline styles
 * on <html>/<body>).
 */
import type { OrgThemePreset } from '../types';

export type ThemeTokenName =
  | 'background'
  | 'foreground'
  | 'card'
  | 'card-foreground'
  | 'popover'
  | 'popover-foreground'
  | 'primary'
  | 'primary-foreground'
  | 'secondary'
  | 'secondary-foreground'
  | 'muted'
  | 'muted-foreground'
  | 'accent'
  | 'accent-foreground'
  | 'destructive'
  | 'destructive-foreground'
  | 'border'
  | 'input'
  | 'ring';

export interface ThemePresetDefinition {
  /** Colour chips shown in the picker (from theme.json). */
  swatches: readonly string[];
  /** Body text font (Google Fonts family name). */
  fontSans: string;
  /** Title font; equal to fontSans except for argile. */
  fontDisplay: string;
  radius: string;
  light: Record<ThemeTokenName, string>;
  dark: Record<ThemeTokenName, string>;
}

export type OliseThemePreset = Exclude<OrgThemePreset, 'default'>;

export const THEME_PRESET_IDS: readonly OrgThemePreset[] = ['default', 'graphite', 'nebula', 'argile', 'acier', 'sauge'];

/** Swatches of the Établi defaults (index.css), for the picker only. */
export const DEFAULT_THEME_SWATCHES: readonly string[] = ['#FFFFFF', '#F97316', '#1C1917', '#111827'];

export const THEME_PRESETS: Record<OliseThemePreset, ThemePresetDefinition> = {
  graphite: {
    swatches: ['#FFFFFF', '#171717', '#0A0A0A', '#006BFF'],
    fontSans: 'Geist',
    fontDisplay: 'Geist',
    radius: '0.5rem',
    light: {
      'background': '0 0% 100%',
      'foreground': '0 0% 9%',
      'card': '0 0% 100%',
      'card-foreground': '0 0% 9%',
      'popover': '0 0% 100%',
      'popover-foreground': '0 0% 9%',
      'primary': '0 0% 9%',
      'primary-foreground': '0 0% 100%',
      'secondary': '0 0% 94.9%',
      'secondary-foreground': '0 0% 3.9%',
      'muted': '0 0% 96.1%',
      'muted-foreground': '0 0% 40%',
      'accent': '0 0% 94.9%',
      'accent-foreground': '0 0% 3.9%',
      'destructive': '357 70.1% 49.8%',
      'border': '0 0% 89.8%',
      'input': '0 0% 89.8%',
      'ring': '214.8 100% 50%',
      'destructive-foreground': '0 0% 100%',
    },
    dark: {
      'background': '0 0% 3.9%',
      'foreground': '0 0% 83.9%',
      'card': '0 0% 6.7%',
      'card-foreground': '0 0% 83.9%',
      'popover': '0 0% 6.7%',
      'popover-foreground': '0 0% 83.9%',
      'primary': '0 0% 92.9%',
      'primary-foreground': '0 0% 3.9%',
      'secondary': '0 0% 12.2%',
      'secondary-foreground': '0 0% 92.9%',
      'muted': '0 0% 10.2%',
      'muted-foreground': '0 0% 63.1%',
      'accent': '0 0% 12.2%',
      'accent-foreground': '0 0% 92.9%',
      'destructive': '358.1 100% 69%',
      'border': '0 0% 18%',
      'input': '0 0% 18%',
      'ring': '210.2 100% 66.1%',
      'destructive-foreground': '0 0% 100%',
    },
  },
  nebula: {
    swatches: ['#FBFBFC', '#5E6AD2', '#7C66DC', '#08090A'],
    fontSans: 'Inter',
    fontDisplay: 'Inter',
    radius: '0.5rem',
    light: {
      'background': '240 14.3% 98.6%',
      'foreground': '225 9.1% 17.3%',
      'card': '0 0% 100%',
      'card-foreground': '225 9.1% 17.3%',
      'popover': '0 0% 100%',
      'popover-foreground': '225 9.1% 17.3%',
      'primary': '233.8 56.3% 59.6%',
      'primary-foreground': '0 0% 100%',
      'secondary': '231.4 70% 96.1%',
      'secondary-foreground': '210 6.2% 6.3%',
      'muted': '240 8.3% 95.3%',
      'muted-foreground': '218.2 4.9% 44.1%',
      'accent': '252.9 70% 96.1%',
      'accent-foreground': '210 6.2% 6.3%',
      'destructive': '356.6 67.7% 53.9%',
      'border': '228 9.8% 90%',
      'input': '228 9.8% 90%',
      'ring': '233.8 56.3% 59.6%',
      'destructive-foreground': '0 0% 100%',
    },
    dark: {
      'background': '210 11.1% 3.5%',
      'foreground': '217.5 20.5% 84.7%',
      'card': '210 6.2% 6.3%',
      'card-foreground': '217.5 20.5% 84.7%',
      'popover': '210 6.2% 6.3%',
      'popover-foreground': '217.5 20.5% 84.7%',
      'primary': '233.8 56.3% 59.6%',
      'primary-foreground': '0 0% 100%',
      'secondary': '231.8 31.7% 12.5%',
      'secondary-foreground': '180 6.7% 97.1%',
      'muted': '225 8.3% 9.4%',
      'muted-foreground': '218.6 6.4% 56.9%',
      'accent': '248.9 30.7% 15.6%',
      'accent-foreground': '180 6.7% 97.1%',
      'destructive': '0 78.7% 63.1%',
      'border': '222.9 9.1% 15.1%',
      'input': '222.9 9.1% 15.1%',
      'ring': '233.8 100% 75.5%',
      'destructive-foreground': '0 0% 100%',
    },
  },
  argile: {
    swatches: ['#F6F3EC', '#B35532', '#2F6E8F', '#1B1814'],
    fontSans: 'Instrument Sans',
    fontDisplay: 'Fraunces',
    radius: '0.625rem',
    light: {
      'background': '42 35.7% 94.5%',
      'foreground': '34.3 13.7% 20%',
      'card': '40 100% 98.8%',
      'card-foreground': '34.3 13.7% 20%',
      'popover': '40 100% 98.8%',
      'popover-foreground': '34.3 13.7% 20%',
      'primary': '16.3 56.3% 44.9%',
      'primary-foreground': '0 0% 100%',
      'secondary': '24 58.1% 91.6%',
      'secondary-foreground': '33.3 17% 10.4%',
      'muted': '41.2 32% 90.2%',
      'muted-foreground': '34.3 10.1% 40.6%',
      'accent': '24 58.1% 91.6%',
      'accent-foreground': '33.3 17% 10.4%',
      'destructive': '4.4 58.6% 45.5%',
      'border': '40 26.6% 84.5%',
      'input': '40 26.6% 84.5%',
      'ring': '17.4 54.3% 49.8%',
      'destructive-foreground': '0 0% 100%',
    },
    dark: {
      'background': '34.3 14.9% 9.2%',
      'foreground': '37.1 23.6% 82.5%',
      'card': '33.3 14.8% 12%',
      'card-foreground': '37.1 23.6% 82.5%',
      'popover': '33.3 14.8% 12%',
      'popover-foreground': '37.1 23.6% 82.5%',
      'primary': '14.8 63.1% 59.6%',
      'primary-foreground': '34.3 14.9% 9.2%',
      'secondary': '19.1 30.3% 17.3%',
      'secondary-foreground': '38.6 36.8% 92.5%',
      'muted': '32.7 14.7% 14.7%',
      'muted-foreground': '35 12.1% 61.2%',
      'accent': '19.1 30.3% 17.3%',
      'accent-foreground': '38.6 36.8% 92.5%',
      'destructive': '5.8 73.1% 66.5%',
      'border': '34.3 13.7% 20%',
      'input': '34.3 13.7% 20%',
      'ring': '14.8 63.1% 59.6%',
      'destructive-foreground': '0 0% 100%',
    },
  },
  acier: {
    swatches: ['#F4F4F4', '#0F62FE', '#8A3FFC', '#161616'],
    fontSans: 'IBM Plex Sans',
    fontDisplay: 'IBM Plex Sans',
    radius: '0.125rem',
    light: {
      'background': '0 0% 95.7%',
      'foreground': '0 0% 14.9%',
      'card': '0 0% 100%',
      'card-foreground': '0 0% 14.9%',
      'popover': '0 0% 100%',
      'popover-foreground': '0 0% 14.9%',
      'primary': '219.2 99.2% 52.7%',
      'primary-foreground': '0 0% 100%',
      'secondary': '213.3 100% 96.5%',
      'secondary-foreground': '0 0% 8.6%',
      'muted': '0 0% 91%',
      'muted-foreground': '0 0% 32.2%',
      'accent': '258.5 100% 97.5%',
      'accent-foreground': '0 0% 8.6%',
      'destructive': '356.8 75.8% 48.6%',
      'border': '0 0% 77.6%',
      'input': '0 0% 77.6%',
      'ring': '219.2 99.2% 52.7%',
      'destructive-foreground': '0 0% 100%',
    },
    dark: {
      'background': '0 0% 8.6%',
      'foreground': '0 0% 87.8%',
      'card': '0 0% 14.9%',
      'card-foreground': '0 0% 87.8%',
      'popover': '0 0% 14.9%',
      'popover-foreground': '0 0% 87.8%',
      'primary': '219.2 99.2% 52.7%',
      'primary-foreground': '0 0% 100%',
      'secondary': '218.1 33.5% 17.4%',
      'secondary-foreground': '0 0% 95.7%',
      'muted': '0 0% 22.4%',
      'muted-foreground': '0 0% 65.9%',
      'accent': '262.8 26.8% 21.2%',
      'accent-foreground': '0 0% 95.7%',
      'destructive': '356.9 94.5% 64.1%',
      'border': '0 0% 32.2%',
      'input': '0 0% 32.2%',
      'ring': '0 0% 100%',
      'destructive-foreground': '0 0% 100%',
    },
  },
  sauge: {
    swatches: ['#FCFCFC', '#1F8A5B', '#1B8F5E', '#0E7490', '#121212'],
    fontSans: 'Plus Jakarta Sans',
    fontDisplay: 'Plus Jakarta Sans',
    radius: '0.625rem',
    light: {
      'background': '0 0% 98.8%',
      'foreground': '144 5.5% 17.8%',
      'card': '0 0% 100%',
      'card-foreground': '144 5.5% 17.8%',
      'popover': '0 0% 100%',
      'popover-foreground': '144 5.5% 17.8%',
      'primary': '153.6 63.3% 33.1%',
      'primary-foreground': '0 0% 100%',
      'secondary': '148 48.4% 93.9%',
      'secondary-foreground': '0 0% 9%',
      'muted': '120 7.7% 94.9%',
      'muted-foreground': '144 4.7% 42%',
      'accent': '148.4 51.4% 92.7%',
      'accent-foreground': '0 0% 9%',
      'destructive': '2.3 64.6% 47.6%',
      'border': '135 6.7% 88.2%',
      'input': '135 6.7% 88.2%',
      'ring': '153.1 60.2% 52.7%',
      'destructive-foreground': '0 0% 100%',
    },
    dark: {
      'background': '0 0% 7.1%',
      'foreground': '0 0% 78.4%',
      'card': '0 0% 10.2%',
      'card-foreground': '0 0% 78.4%',
      'popover': '0 0% 10.2%',
      'popover-foreground': '0 0% 78.4%',
      'primary': '153.1 60.2% 52.7%',
      'primary-foreground': '152.9 58.5% 10.4%',
      'secondary': '153.1 27.2% 12.5%',
      'secondary-foreground': '0 0% 92.9%',
      'muted': '0 0% 13.7%',
      'muted-foreground': '0 0% 56.1%',
      'accent': '153.1 29.6% 13.5%',
      'accent-foreground': '0 0% 92.9%',
      'destructive': '4.9 83% 65.5%',
      'border': '0 0% 18%',
      'input': '0 0% 18%',
      'ring': '153.1 60.2% 52.7%',
      'destructive-foreground': '0 0% 100%',
    },
  },
};

export function isOliseThemePreset(value: string | null | undefined): value is OliseThemePreset {
  return !!value && value !== 'default' && value in THEME_PRESETS;
}

const FALLBACK_STACK = "system-ui, -apple-system, 'Segoe UI', sans-serif";

export function fontStack(font: string): string {
  return `'${font}', ${FALLBACK_STACK}`;
}

/** One Google Fonts stylesheet for the preset's families. */
export function themePresetFontHref(preset: ThemePresetDefinition): string {
  const families = Array.from(new Set([preset.fontSans, preset.fontDisplay]));
  const query = families.map((f) => `family=${f.replace(/\s+/g, '+')}:wght@400;500;600;700`).join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}

const declarations = (tokens: Record<ThemeTokenName, string>, radius: string): string =>
  [...Object.entries(tokens).map(([name, value]) => `--${name}: ${value};`), `--radius: ${radius};`].join(' ');

/**
 * Stylesheet for a preset. `:root:not(.dark)` / `:root.dark` outrank the
 * `:root` / `.dark` rules of index.css whatever the load order; the org's
 * branding is set inline and so still wins. Fonts are left out when the org
 * branding sets its own font.
 */
export function buildThemePresetCss(preset: ThemePresetDefinition, withFonts: boolean): string {
  const rules = [
    `:root:not(.dark) { ${declarations(preset.light, preset.radius)} }`,
    `:root.dark { ${declarations(preset.dark, preset.radius)} }`,
  ];
  if (withFonts) {
    rules.push(`body { font-family: ${fontStack(preset.fontSans)}; }`);
    if (preset.fontDisplay !== preset.fontSans) {
      rules.push(`h1, h2, h3 { font-family: ${fontStack(preset.fontDisplay)}; }`);
    }
  }
  return rules.join('\n');
}

/** Colour chips of any preset, the Établi defaults included. */
export const presetSwatches = (preset: OrgThemePreset): readonly string[] =>
  isOliseThemePreset(preset) ? THEME_PRESETS[preset].swatches : DEFAULT_THEME_SWATCHES;
